import React from 'react';
import { clsx } from 'clsx';

import {
  PolymorphicProps,
  PolymorphicRef
} from '../types/polymorphic/polymorphic.types';
import { HelperProps, helpers } from './helpers/helpers';

const defaultElement = 'div' as const;

type BaseElementProps = {
  /** Content of the element. */
  children?: React.ReactNode;
} & HelperProps;

export type ElementProps<T extends React.ElementType = typeof defaultElement> =
  PolymorphicProps<T, BaseElementProps>;

type ElementComponent = <T extends React.ElementType = typeof defaultElement>(
  props: PolymorphicProps<T, ElementProps<T>> & {
    ref?: PolymorphicRef<T>;
  }
) => JSX.Element | null;

export const Element: ElementComponent = React.forwardRef(
  <T extends React.ElementType = typeof defaultElement>(
    props: ElementProps<T>,
    ref: PolymorphicRef<T>
  ) => {
    const { className = undefined, renderAs, ...allProps } = props;
    const RenderAs = renderAs || defaultElement;
    const processedProps = helpers.base(allProps);

    return (
      <RenderAs
        ref={ref}
        className={clsx(className, helpers.classnames(allProps) || undefined)}
        {...processedProps}
      />
    );
  }
);

export namespace Element {
  export type Props<T extends React.ElementType = typeof defaultElement> =
    ElementProps<T>;
}
